import { Box } from '@gassan-ui'
import { Icon } from '@rolex-ui'
import React, { FC, Fragment } from 'react'
import styled, { css } from 'styled-components'

// https://gist.github.com/kottenator/9d936eb3e4e3c3e02598#gistcomment-2664601
const pagination = (currentPage: number, pageCount: number) => {
  // The delta is the amount of pages spread around the current page
  const delta = currentPage < 3 ? 2 : 1

  let range = []
  for (
    let i = Math.max(2, currentPage - delta);
    i <= Math.min(pageCount - 1, currentPage + delta);
    i++
  ) {
    range.push(i)
  }

  if (currentPage - delta > 2) {
    range.unshift('...')
  }
  if (currentPage + delta < pageCount - 1) {
    range.push('...')
  }

  range.unshift(1)
  range.push(pageCount)

  return range
}

const item = css`
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 400;
  outline: 0;
`

const Button = styled.button<{ active?: boolean }>`
  ${item};
  transition: color 0.2s ease-in-out;
  color: ${p => (p.active ? p.theme.rolex.colors.shades[900] : p.theme.rolex.colors.shades[700])};
  pointer-events: ${p => (p.active ? 'none' : 'all')};
  &:hover {
    color: ${p => p.theme.rolex.colors.shades[900]};
  }
`
const Dot = styled.span`
  ${item};
  color: ${p => p.theme.rolex.colors.shades[700]};
`

interface Props {
  limit: number
  currentPage: number
  totalCount: number
  setPage: (page: number) => void
}

const Pagination: FC<Props> = ({ limit, currentPage, totalCount, setPage }) => {
  const totalPages = Math.ceil(totalCount / limit)
  const canGoBack = currentPage > 1
  const canGoToFirst = canGoBack
  const canGoForward = currentPage < totalPages
  const canGoToLast = currentPage !== totalPages

  const pages =
    totalPages <= 5
      ? Array.from(new Array(totalPages)).map((_, i) => i + 1)
      : pagination(currentPage, totalPages)

  const handleGoToPage = (page: number) => () => {
    setPage(page)
    window.scrollTo({ top: 300, left: 0, behavior: 'smooth' })
  }

  if (totalPages === 1) {
    return null
  }

  return (
    <Box width="100%" display="flex" justifyContent="center" pt="2rem" pb="3.75rem">
      {canGoToFirst && <Button onClick={handleGoToPage(1)}>{'|<'}</Button>}
      {canGoBack && (
        <Button onClick={handleGoToPage(currentPage - 1)}>
          <Icon icon="chevron-left" size={22} />
        </Button>
      )}
      {pages.map((page, index) =>
        page === '...' ? (
          <Fragment key={index}>
            <Dot>•</Dot>
            <Dot>•</Dot>
            <Dot>•</Dot>
          </Fragment>
        ) : (
          <Button key={index} active={page === currentPage} onClick={handleGoToPage(page)}>
            {page}
          </Button>
        )
      )}
      {canGoForward && (
        <Button onClick={handleGoToPage(currentPage + 1)}>
          <Icon icon="chevron-right" size={22} />
        </Button>
      )}
      {canGoToLast && <Button onClick={handleGoToPage(totalCount)}>{'>|'}</Button>}
    </Box>
  )
}

export default Pagination
